import { publicVendorApi } from '../api/modules/publicVendor.api';
import { getApiErrorMessage } from '../api/client';
import { USE_API } from '../config/api';
import { vendorFromApi, vendorToApi, indentFromApi, indentToApi } from '../models';

export async function registerPublicVendor(ui) {
  if (!USE_API) throw new Error('Public registration requires API mode');
  try {
    const body = vendorToApi(ui);
    // Public register must not send verification-only fields
    delete body.localProfessional;
    delete body.majorCategory;
    delete body.verificationStatus;
    const data = await publicVendorApi.register(body);
    const vendorRow = data?.vendor ?? data;
    const vendor = vendorFromApi(vendorRow);
    return {
      ...vendor,
      uploadToken: data?.uploadToken || null,
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Registration failed'));
  }
}

/** Public — no staff login required. */
export async function getPublicRegistrationStatus() {
  if (!USE_API) {
    return { enabled: true, disabledReason: null };
  }
  try {
    const data = await publicVendorApi.registrationStatus();
    return {
      enabled: data?.enabled !== false,
      disabledReason: data?.disabledReason || null,
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load registration status'));
  }
}

export async function getPortalProfile(token) {
  if (!USE_API) throw new Error('Portal requires API mode');
  try {
    return vendorFromApi(await publicVendorApi.tokenProfile(token));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Invalid portal link'));
  }
}

/**
 * Portal page bootstrap — portal token only (no staff bearer).
 * Returns { vendor, equipmentTypes, indenterFormEnabled }.
 */
export async function getPortalBootstrap(token) {
  if (!USE_API) throw new Error('Portal requires API mode');
  try {
    const data = await publicVendorApi.portalBootstrap(token);
    return {
      vendor: vendorFromApi(data?.vendor ?? data),
      equipmentTypes: Array.isArray(data?.equipmentTypes) ? data.equipmentTypes : [],
      indenterFormEnabled: data?.indenterFormEnabled !== false,
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Invalid portal link'));
  }
}

export async function listPortalIndents(token, params = {}) {
  if (!USE_API) throw new Error('Portal requires API mode');
  try {
    const result = await publicVendorApi.tokenIndents(token, params);
    const items = Array.isArray(result) ? result : result?.items || [];
    return items.map(indentFromApi);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load requests'));
  }
}

export async function getPortalIndent(token, indentId) {
  if (!USE_API) throw new Error('Portal requires API mode');
  try {
    return indentFromApi(await publicVendorApi.tokenIndentById(token, indentId));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load request'));
  }
}

export async function createPortalIndent(token, ui) {
  if (!USE_API) throw new Error('Portal requires API mode');
  try {
    return indentFromApi(await publicVendorApi.createTokenIndent(token, indentToApi(ui)));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to create request'));
  }
}

export async function assignPortalIndent(token, indentId, body = {}) {
  if (!USE_API) throw new Error('Portal requires API mode');
  try {
    return indentFromApi(await publicVendorApi.assignTokenIndent(token, indentId, body));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to give work'));
  }
}

export async function releasePortalIndent(token, indentId, body = {}) {
  if (!USE_API) throw new Error('Portal requires API mode');
  try {
    return indentFromApi(await publicVendorApi.releaseTokenIndent(token, indentId, body));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to release equipment'));
  }
}
